import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import useSetExport from '@components/export/useSetExport';
import {
  allFiltersSelector,
  selectedDimensionSelector,
  selectedMetricsSelector,
} from '@store/metricLens/base/selectors';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import { getMetricMetaByKey, getPercentageMetricForSeries } from '@utils/metrics/metricHelpers';
import { historicalRangeTotalDataSelector } from '@store/metricLens/historicalRangeData/selectors';
import { historicalRangeDataSelector } from '../utils';

// generate columns per filter
const getColumnsPerFilter = (col, count) => {
  const columns: any[] = [];
  const { title } = col;
  for (let i = 0; i < count; i++) {
    columns.push({ ...col, title: `${title} F${i + 1}`, valueIdx: i });
  }
  return columns;
};

function useTableExport() {
  const dimension = useSelector(selectedDimensionSelector);
  const dimensionsList: any[] = useSelector(dimensionsListSelector);
  const metrics: any[] = useSelector(selectedMetricsSelector);
  const filters: any[] = useSelector(allFiltersSelector);
  const totals = useSelector(historicalRangeTotalDataSelector);
  const data = useSelector(historicalRangeDataSelector);

  const dimensionTitle = useMemo(
    () =>
      get(
        dimensionsList.find((r) => r.groupBy === dimension),
        'name',
        '',
      ),
    [dimension, dimensionsList],
  );

  const { headers, totalsRow } = useMemo(() => {
    const headers: any = [
      {
        title: dimensionTitle,
        key: 'name',
        type: 'dimension',
      },
    ];

    const totalsRow: any = {};

    metrics.forEach((metric) => {
      const metricInfo = getMetricMetaByKey({ metricKey: metric });
      // TODO: support local percentage metrics
      const pctMetric = getPercentageMetricForSeries(metric);

      // metric
      headers.push(
        ...getColumnsPerFilter(
          {
            title: `${metricInfo.short}`,
            key: metric,
            type: 'multi-filter',
          },
          filters.length,
        ),
      );

      totalsRow[metric] = [];

      // percentage metric
      if (metric !== pctMetric) {
        headers.push(
          ...getColumnsPerFilter(
            {
              title: `${metricInfo.short} (%)`,
              key: pctMetric,
              type: 'multi-filter',
            },
            filters.length,
          ),
        );
        totalsRow[pctMetric] = [];
      }
    });

    return { headers, totalsRow };
  }, [metrics, dimensionTitle, filters.length]);

  useMemo(() => {
    Object.keys(totalsRow).forEach((metric) => {
      totalsRow[metric] = totals.map((row) => row[metric]);
    });
    totalsRow.id = 'totals';
    totalsRow.name = ['Totals'];
    totalsRow.value = ['Totals'];
  }, [totalsRow, totals]);

  useSetExport({
    pageKey: 'metriclens',
    sectionKey: 'ml-content',
    widgetKey: `table`,
    headers,
    data: data.concat(totalsRow),
    widgetInfo: {
      title: '',
    },
  });
}

export default useTableExport;
